/**
 * Billing Service
 * Handles subscription management, payment methods, and billing history
 */

import api, { setAccessToken as setGlobalAccessToken } from './api';
import logger from '../utils/logger';

class BillingService {
  constructor() {
    this.endpoints = {
      subscription: '/api/billing/subscription',
      paymentMethods: '/api/billing/payment-methods',
      setupIntent: '/api/billing/setup-intent',
      history: '/api/billing/history',
    };
  }

  /**
   * Set access token on the centralized Axios instance
   * @param {string} token - Auth0 access token
   */
  setAccessToken(token) {
    setGlobalAccessToken(token);
  }

  /**
   * Make an authenticated API request via the centralized Axios instance
   * @param {string} endpoint - Relative endpoint path
   * @param {string} method - HTTP method
   * @param {Object} [data] - Request body
   */
  async request(endpoint, method = 'GET', data = undefined) {
    const config = { method };
    if (data !== undefined) {
      config.data = data;
    }

    const response = await api(endpoint, config);

    // Axios throws on non-2xx, so if we get here the request succeeded.
    // Mirror the original shape: return response.data directly.
    return response.data;
  }

  // =========================================================================
  // SUBSCRIPTION METHODS
  // =========================================================================

  /**
   * Get current subscription details
   * @returns {Promise<Object>} Subscription info
   */
  async getSubscription() {
    try {
      const result = await this.request(this.endpoints.subscription);
      return result.data || result;
    } catch (error) {
      logger.error('Failed to get subscription:', error);
      throw error;
    }
  }

  /**
   * Create or upgrade subscription
   * @param {string} planType - recurring package key, currently 'rely_membership'
   * @returns {Promise<Object>} New subscription details
   */
  async createSubscription(planType) {
    try {
      const result = await this.request(this.endpoints.subscription, 'POST', {
        plan_type: planType,
      });
      return result.data || result;
    } catch (error) {
      logger.error('Failed to create subscription:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription (at end of billing period)
   * @returns {Promise<Object>} Cancellation details
   */
  async cancelSubscription() {
    try {
      const result = await this.request(this.endpoints.subscription, 'DELETE');
      return result.data || result;
    } catch (error) {
      logger.error('Failed to cancel subscription:', error);
      throw error;
    }
  }

  /**
   * Reactivate a canceled subscription
   * @returns {Promise<Object>} Reactivated subscription details
   */
  async reactivateSubscription() {
    try {
      const result = await this.request(
        `${this.endpoints.subscription}/reactivate`,
        'POST'
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to reactivate subscription:', error);
      throw error;
    }
  }

  // =========================================================================
  // PAYMENT METHODS
  // =========================================================================

  /**
   * Get all saved payment methods
   * @returns {Promise<Array>} List of payment methods
   */
  async getPaymentMethods() {
    try {
      const result = await this.request(this.endpoints.paymentMethods);
      return result.data || [];
    } catch (error) {
      logger.error('Failed to get payment methods:', error);
      throw error;
    }
  }

  /**
   * Create a SetupIntent for adding a new payment method
   * @returns {Promise<Object>} { client_secret, setup_intent_id }
   */
  async createSetupIntent() {
    try {
      const result = await this.request(this.endpoints.setupIntent, 'POST');
      return result.data || result;
    } catch (error) {
      logger.error('Failed to create setup intent:', error);
      throw error;
    }
  }

  /**
   * Add a payment method (after Stripe confirmation)
   * @param {string} stripePaymentMethodId - Stripe PaymentMethod ID
   * @param {boolean} setAsDefault - Whether to set as default
   * @returns {Promise<Object>} Added payment method details
   */
  async addPaymentMethod(stripePaymentMethodId, setAsDefault = true) {
    try {
      const result = await this.request(this.endpoints.paymentMethods, 'POST', {
        stripe_payment_method_id: stripePaymentMethodId,
        set_as_default: setAsDefault,
      });
      return result.data || result;
    } catch (error) {
      logger.error('Failed to add payment method:', error);
      throw error;
    }
  }

  /**
   * Remove a payment method
   * @param {string} paymentMethodId - Internal payment method ID
   * @returns {Promise<boolean>} Success status
   */
  async removePaymentMethod(paymentMethodId) {
    try {
      await this.request(
        `${this.endpoints.paymentMethods}/${paymentMethodId}`,
        'DELETE'
      );
      return true;
    } catch (error) {
      logger.error('Failed to remove payment method:', error);
      throw error;
    }
  }

  /**
   * Set a payment method as default
   * @param {string} paymentMethodId - Internal payment method ID
   * @returns {Promise<boolean>} Success status
   */
  async setDefaultPaymentMethod(paymentMethodId) {
    try {
      await this.request(
        `${this.endpoints.paymentMethods}/${paymentMethodId}/default`,
        'PUT'
      );
      return true;
    } catch (error) {
      logger.error('Failed to set default payment method:', error);
      throw error;
    }
  }

  // =========================================================================
  // BILLING HISTORY
  // =========================================================================

  /**
   * Get billing history
   * @param {number} limit - Number of records to fetch (default 10)
   * @returns {Promise<Array>} Billing history records
   */
  async getBillingHistory(limit = 10) {
    try {
      const result = await this.request(
        `${this.endpoints.history}?limit=${limit}`
      );
      return result.data || [];
    } catch (error) {
      logger.error('Failed to get billing history:', error);
      throw error;
    }
  }
}

// Export singleton instance
const billingService = new BillingService();
export default billingService;

// Also export the class for testing
export { BillingService };
